// ItemCard — 통합 Explorer 아이템 카드 컴포넌트.
// 타입 아이콘·이름·태그·만료 D-day·즐겨찾기 별·복사 버튼 표시.
// 검색 q 매칭 시 이름 하이라이트.
// 태그 클릭 → Explorer ?tag= 필터로 이동.
//
// Phase 2 DnD 확장:
// - useDraggable: 카드 전체를 드래그 대상으로 등록
// - 드래그 중 반투명 효과 + 드래그 핸들 아이콘 표시
// - 모바일: TouchSensor 250ms 딜레이로 탭과 드래그 충돌 방지 (DndProvider에서 설정)
//
// U44 다중 선택 확장:
// - selected prop: 선택 시 좌측 accent 바 + 약한 배경 (item-card--selected 클래스)
// - 체크박스 표시 (선택 모드 진입 시 노출)

import { useNavigate } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useItemMutations } from '../hooks/useItems';
import { useToast } from './Toast';
import { formatApiError } from '../api/client';
import { TagPill } from './TagPill';
import { highlightSegments } from '../utils/searchHighlight';
import type { DndDraggableData } from '../utils/dndHelpers';
import type { ItemMeta } from '../api/items';
import type { Tag } from '../api/tags';

// ─── 타입 아이콘·레이블 맵 ───────────────────────────────

const TYPE_ICON: Record<string, string> = {
  credential_account: '👤',
  credential_api_key: '🔑',
  credential_server: '🖥️',
  credential_env: '📋',
  credential_auth: '🛡️',
  note: '📝',
  server: '🗄️',
  domain: '🌐',
  service: '⚙️',
  subscription: '💳',
  file: '📁',
  checklist: '✅',
};

const TYPE_LABEL: Record<string, string> = {
  credential_account: '계정',
  credential_api_key: 'API 키',
  credential_server: '서버',
  credential_env: '.env',
  credential_auth: '인증',
  note: '메모',
  server: '서버 인프라',
  domain: '도메인',
  service: '서비스',
  subscription: '구독',
  file: '파일',
  checklist: '체크리스트',
};

// ─── 이름 하이라이트 렌더 ─────────────────────────────────

function HighlightedName({ name, query }: { name: string; query: string }) {
  if (!query) return <span>{name}</span>;
  const segments = highlightSegments(name, query);
  return (
    <span>
      {segments.map((seg, i) =>
        seg.highlight ? <mark key={i}>{seg.text}</mark> : <span key={i}>{seg.text}</span>
      )}
    </span>
  );
}

// ─── ItemCard 컴포넌트 ────────────────────────────────────

interface ItemCardProps {
  item: ItemMeta;
  /** 카드 클릭 핸들러 (상세 모달 열기용) */
  onClick?: (id: number) => void;
  /** 현재 검색어 (이름 하이라이트용) */
  searchQuery?: string;
  /** payload 검색에서 매칭됐을 때 배지 표시 여부 */
  showPayloadMatch?: boolean;
  /** 다중 선택 모드: 이 카드가 선택됐는지 여부 */
  selected?: boolean;
  /** 다중 선택 클릭 핸들러 (selected prop 제공 시 우선 사용) */
  onSelectClick?: (id: number, e: React.MouseEvent) => void;
}

export function ItemCard({
  item,
  onClick,
  searchQuery = '',
  showPayloadMatch = false,
  selected = false,
  onSelectClick,
}: ItemCardProps) {
  const { pinItem, unpinItem } = useItemMutations();
  const toast = useToast();
  const navigate = useNavigate();

  // 태그 클릭 → Explorer(루트) ?tag= 필터로 이동
  const handleTagClick = (tag: Tag) => {
    navigate(`/?tag=${encodeURIComponent(tag.name)}`);
  };

  const isPinned = item.pinned === 1;

  // ─── DnD — 드래그 대상 등록 ─────────────────────────────

  // U45: selectedIds는 ItemCard 자신이 모르므로 [itemId]로 초기화.
  // 실제 멀티 드래그 여부는 DndProvider.handleDragStart에서
  // selectedIdsRef.current를 참조하여 결정한다.
  const draggableData: DndDraggableData = {
    type: 'item',
    itemId: item.id,
    selectedIds: [item.id],
  };

  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `item-${item.id}`,
    data: draggableData,
  });

  const dragStyle: React.CSSProperties = transform
    ? { transform: CSS.Translate.toString(transform) }
    : {};

  // ─── 다중 선택 클릭 핸들러 ────────────────────────────────

  const handleCardClick = (e: React.MouseEvent) => {
    // onSelectClick 제공 시 다중 선택 모드 우선 처리
    if (onSelectClick) {
      onSelectClick(item.id, e);
      return;
    }
    // Cmd/Ctrl 또는 Shift 클릭이면 단순 onClick 무시 (Explorer에서 처리)
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    onClick?.(item.id);
  };

  // ─── 핀 토글 핸들러 ──────────────────────────────────────

  const handlePinToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (isPinned) {
        await unpinItem.mutateAsync(item.id);
      } else {
        await pinItem.mutateAsync(item.id);
      }
    } catch (err) {
      toast.error(formatApiError(err, '즐겨찾기 변경 실패'));
    }
  };

  const icon = item.icon ?? TYPE_ICON[item.type] ?? '📄';
  const typeLabel = TYPE_LABEL[item.type] ?? item.type;

  return (
    <div
      ref={setDragRef}
      className={[
        'item-card',
        isPinned ? 'item-card--pinned' : '',
        isDragging ? 'item-card--dragging' : '',
        selected ? 'item-card--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...dragStyle,
        opacity: isDragging ? 0.4 : 1,
      }}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onSelectClick) {
            // 키보드로 선택 모드 토글 (modifier key 없이 Enter/Space)
            onSelectClick(item.id, e as unknown as React.MouseEvent);
          } else {
            onClick?.(item.id);
          }
        }
      }}
      aria-label={`${typeLabel}: ${item.name}`}
      aria-pressed={onSelectClick ? selected : undefined}
    >
      {/* 다중 선택 체크박스 — onSelectClick 제공 시 노출 */}
      {onSelectClick && (
        <span
          className={`item-card__checkbox${selected ? ' item-card__checkbox--checked' : ''}`}
          aria-hidden="true"
          onClick={(e) => {
            e.stopPropagation();
            onSelectClick(item.id, e);
          }}
        >
          {selected ? '✓' : '○'}
        </span>
      )}

      {/* 드래그 핸들 — 카드 우상단 (시각 피드백) */}
      <span
        className="item-card__drag-handle"
        aria-hidden="true"
        title="드래그하여 그룹 변경"
        {...dragAttributes}
        {...dragListeners}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        ⠿
      </span>

      {/* 카드 헤더 — 아이콘 + 이름 + 즐겨찾기 버튼 */}
      <div className="item-card__header">
        <span className="item-card__icon" aria-hidden="true">{icon}</span>
        <span className="item-card__name">
          <HighlightedName name={item.name} query={searchQuery} />
        </span>
        <button
          type="button"
          className={`item-card__pin${isPinned ? ' item-card__pin--active' : ''}`}
          onClick={handlePinToggle}
          aria-label={isPinned ? '즐겨찾기 해제' : '즐겨찾기 추가'}
          aria-pressed={isPinned}
          disabled={pinItem.isPending || unpinItem.isPending}
        >
          {isPinned ? '⭐' : '☆'}
        </button>
      </div>

      {/* 타입 뱃지 */}
      <div className="item-card__meta">
        <span className="badge badge--type">{typeLabel}</span>
        {/* payload 검색 매칭 배지 */}
        {showPayloadMatch && item.match?.in_payload && (
          <span className="badge badge--payload" title="내용에서 매칭됨">
            내용 매칭
          </span>
        )}
        {/* 사용 횟수 — 예전 푸터에서 이동 (2026-10-03) */}
        {item.use_count > 0 && (
          <span className="item-card__use-count" title={`${item.use_count}회 사용`}>
            ×{item.use_count}
          </span>
        )}
      </div>

      {/* 태그 — TagPill 컴포넌트로 색상 점·클릭 이동 표시 */}
      {item.tags.length > 0 && (
        <div className="item-card__tags">
          {item.tags.slice(0, 5).map((name) => (
            <TagPill
              key={name}
              tag={{ id: 0, name, color: null }}
              onClick={handleTagClick}
              size="sm"
            />
          ))}
          {item.tags.length > 5 && (
            <span className="tag-chip tag-chip--more">+{item.tags.length - 5}</span>
          )}
        </div>
      )}

      {/* 이름 복사는 카드에서 제거 — 상세 패널 이름 옆 복사 버튼으로 이동 (2026-10-03 마스터 지시) */}
    </div>
  );
}
